import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Button,
  toast,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { updateDbtCloudConnection, updateDbtCloudSchedule } from "@/helpers/apis";
import {
  DbtCloudConnectionResponse,
  type DbtCloudConnectionUpdateRequest,
  type DbtCloudScheduleUpdateRequest,
  DBT_CLOUD_QUERY_KEYS,
} from "../../types";
import { ScheduleFormFields } from "./ScheduleFormFields";
import {
  parseCronExpression,
  buildCronExpression,
} from "../DatastoreAdd/DatastoreConnections/scheduleUtils";

// Combined form schema
const EditFormSchema = z.object({
  name: z.string().min(1).max(128).optional(),
  api_token: z.string().optional(),
  api_base_url: z.string().optional(),
  schedule_frequency: z.string().optional(),
  schedule_time: z.string().optional(),
  schedule_day_of_week: z.string().optional(),
  schedule_day_of_month: z.string().optional(),
});

type EditFormValues = z.infer<typeof EditFormSchema>;

interface EditConnectionDialogProps {
  connection: DbtCloudConnectionResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditConnectionDialog = ({
  connection,
  open,
  onOpenChange,
}: EditConnectionDialogProps) => {
  const { connectionId } = useParams<{ connectionId: string }>();
  const queryClient = useQueryClient();

  // Parse existing cron to get initial frequency/time values
  const initialSchedule = parseCronExpression(connection.schedule?.cron_expr || "0 0 * * *");

  const form = useForm<EditFormValues>({
    resolver: zodResolver(EditFormSchema),
    defaultValues: {
      name: connection.name,
      api_base_url: connection.api_base_url,
      schedule_frequency: initialSchedule.frequency,
      schedule_time: initialSchedule.time,
      schedule_day_of_week: initialSchedule.dayOfWeek,
      schedule_day_of_month: initialSchedule.dayOfMonth,
    },
  });

  useEffect(() => {
    if (open) {
      const schedule = parseCronExpression(connection.schedule?.cron_expr || "0 0 * * *");
      form.reset({
        name: connection.name,
        api_base_url: connection.api_base_url,
        api_token: "",
        schedule_frequency: schedule.frequency,
        schedule_time: schedule.time,
        schedule_day_of_week: schedule.dayOfWeek,
        schedule_day_of_month: schedule.dayOfMonth,
      });
    }
  }, [open, connection, form]);

  const invalidateQueries = () => {
    queryClient.invalidateQueries({
      queryKey: [DBT_CLOUD_QUERY_KEYS.CONNECTION, connectionId],
    });
    queryClient.invalidateQueries({
      queryKey: [DBT_CLOUD_QUERY_KEYS.CONNECTIONS],
    });
  };

  const { mutateAsync: updateConnection, isPending: isUpdatingConnection } = useMutation({
    mutationFn: (data: DbtCloudConnectionUpdateRequest) =>
      updateDbtCloudConnection(connection.id, data),
  });

  const { mutateAsync: updateSchedule, isPending: isUpdatingSchedule } = useMutation({
    mutationFn: (data: DbtCloudScheduleUpdateRequest) =>
      updateDbtCloudSchedule(connection.id, data),
  });

  const isPending = isUpdatingConnection || isUpdatingSchedule;

  const onSubmit = async (data: EditFormValues) => {
    try {
      const connectionPayload: DbtCloudConnectionUpdateRequest = {};
      if (data.name && data.name !== connection.name) {
        connectionPayload.name = data.name;
      }
      if (data.api_token) {
        connectionPayload.api_token = data.api_token;
      }
      if (data.api_base_url && data.api_base_url !== connection.api_base_url) {
        connectionPayload.api_base_url = data.api_base_url;
      }

      // Build cron expression from frequency and time
      const newCronExpr = buildCronExpression(
        data.schedule_frequency || "daily",
        data.schedule_time || "12:00 AM",
        data.schedule_day_of_week || "1",
        data.schedule_day_of_month || "1"
      );

      const schedulePayload: DbtCloudScheduleUpdateRequest = {};
      if (newCronExpr !== connection.schedule?.cron_expr) {
        schedulePayload.cron_expr = newCronExpr;
      }

      const hasConnectionChanges = Object.keys(connectionPayload).length > 0;
      const hasScheduleChanges = Object.keys(schedulePayload).length > 0;

      if (!hasConnectionChanges && !hasScheduleChanges) {
        onOpenChange(false);
        return;
      }

      const promises: Promise<unknown>[] = [];
      if (hasConnectionChanges) {
        promises.push(updateConnection(connectionPayload));
      }
      if (hasScheduleChanges) {
        promises.push(updateSchedule(schedulePayload));
      }

      await Promise.all(promises);

      toast({
        title: "Connection updated",
        description: "The connection has been updated successfully.",
      });
      invalidateQueries();
      onOpenChange(false);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: (error as Error)?.message || "Failed to update connection",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:al-max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Connection</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <Stack direction="column" gap="md" className="al-py-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Connection Name</FormLabel>
                    <FormControl>
                      <Input placeholder="My dbt Cloud Connection" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="api_token"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Token (optional)</FormLabel>
                    <FormControl>
                      <Input
                        type="password"
                        placeholder="Leave blank to keep existing token"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="api_base_url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>API Base URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="https://cloud.getdbt.com/api/v2/"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <ScheduleFormFields form={form} />
            </Stack>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? "Saving..." : "Save Changes"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
